import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import { Scale, Building2, Loader2, Search, X, AlertCircle, CheckCircle2, FileSignature } from 'lucide-react';
import cultivarService, { type AuthorisedNursery, type IssueLicenseInput, type SublicenceGroup } from '../../services/cultivar';
import contractService from '../../services/contracts';
import { ApiError } from '../../types/api';
import type { ContractFamily, LicensePreview } from '../../types/contracts';
import { Fingerprint } from '../../components/cultivar/contracts/ContractBits';
import { RightsNotice } from '../../components/cultivar/RightsNotice';
import CultivarPicker, { type PickedCultivar } from '../../components/orchard/CultivarPicker';
import organizationService from '../../services/organization';
import { useOrgTimezone, formatLocalDateInTz } from '../../utils/timezone';
import { NURSERY_TYPE_FILTER, ORCHARD_TYPE_FILTER } from '../../constants/organisationTypes';

type Tier = 'nursery_master_sublicence' | 'nursery_propagation' | 'orchard_planting';

// A master sublicence is the odd one out on this screen, and deliberately so.
// The other two tiers licence a cultivar to somebody; a master licences a
// GROUP to a nursery and names no cultivar at all. Everything cultivar-shaped
// below — the rootstock, the tree count, the supplying nursery — is hidden for
// it rather than left on screen reading as optional.
const isMasterTier = (t: Tier) => t === 'nursery_master_sublicence';

interface Contact {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

interface OrgResult {
  id: number;
  name: string;
  stakeholder_types: string[];
}

// A licence names a party. That party has to be an organisation that already
// exists in Factree — there is no such thing as licensing a string. So this is
// a picker, never a text field: it opens on focus showing the organisations
// that qualify, and says out loud when none do rather than leaving an empty
// box that reads like somewhere to type a name.
function OrgSearch({
  label,
  hint,
  stakeholderTypeFilter,
  typeLabel,
  value,
  selectedName,
  onChange,
}: {
  label: string;
  hint?: string;
  /** Comma-separated stakeholder types; the server matches any of them. */
  stakeholderTypeFilter?: string;
  /** How to name the filtered set in prose, e.g. "orchard". */
  typeLabel?: string;
  value?: number;
  selectedName?: string;
  onChange: (id: number, name: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<OrgResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [searched, setSearched] = useState(false);
  // Escape hatch: an organisation can be real and simply not tagged yet. The
  // filter is the default, not a wall — otherwise the only way forward is to
  // go and edit somebody else's organisation record.
  const [showAll, setShowAll] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const noun = typeLabel || 'matching';

  // Browsing beats guessing: focus alone lists who qualifies, so the size of
  // the set is visible before a single key is pressed.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await organizationService.searchOrganizations({
          name: query.trim() || undefined,
          stakeholder_type: showAll ? undefined : stakeholderTypeFilter,
        });
        if (cancelled) return;
        setResults(res.success && res.data ? (res.data as OrgResult[]) : []);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setSearched(true);
        }
      }
    }, 250);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open, showAll, stakeholderTypeFilter]);

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const matchesFilter = (org: OrgResult) => {
    if (!stakeholderTypeFilter) return true;
    const wanted = stakeholderTypeFilter.split(',').map(t => t.trim());
    return (org.stakeholder_types || []).some(t => wanted.includes(t));
  };

  const pick = (org: OrgResult) => {
    onChange(org.id, org.name);
    setOpen(false);
    setQuery('');
    setShowAll(false);
  };

  if (value && selectedName) {
    return (
      <div className="space-y-1">
        <label className="block text-sm font-semibold text-neutral-700">{label}</label>
        {hint && <p className="text-xs text-neutral-500">{hint}</p>}
        <div className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
            <span className="text-sm font-medium text-blue-900 truncate">{selectedName}</span>
            <span className="text-xs text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded flex-shrink-0">ID: {value}</span>
          </div>
          <button type="button" onClick={() => onChange(0, '')} className="text-blue-400 hover:text-blue-600 flex-shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <label className="block text-sm font-semibold text-neutral-700">{label}</label>
      {hint && <p className="text-xs text-neutral-500">{hint}</p>}
      <div className="relative" ref={wrapRef}>
        <div className="relative">
          <input
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={showAll ? 'Search all organisations...' : `Choose a registered ${noun} organisation...`}
            className="w-full pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
          </div>
        </div>

        {/* Typed text is not an answer. Say so while it still reads like one. */}
        {query.trim() !== '' && (
          <p className="mt-1 text-xs text-amber-700">
            Not selected yet — pick an organisation from the list.
          </p>
        )}

        {open && (
          <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
            {results.length > 0 ? (
              results.map(org => {
                const off = !matchesFilter(org);
                return (
                  <button
                    key={org.id}
                    type="button"
                    className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0"
                    onMouseDown={e => { e.preventDefault(); pick(org); }}
                  >
                    <p className="text-sm font-medium text-neutral-900">{org.name}</p>
                    {org.stakeholder_types?.length > 0 ? (
                      <p className="text-xs text-neutral-500">{org.stakeholder_types.join(', ')}</p>
                    ) : (
                      <p className="text-xs text-neutral-400">No organisation type set</p>
                    )}
                    {off && <p className="text-xs text-amber-700">Not tagged as {noun}</p>}
                  </button>
                );
              })
            ) : loading || !searched ? (
              <div className="px-4 py-3 text-sm text-neutral-500">Loading...</div>
            ) : (
              <div className="px-4 py-3 space-y-2">
                <p className="text-sm text-neutral-600">
                  {showAll
                    ? 'No organisations match that name.'
                    : query.trim()
                      ? `No ${noun} organisations match "${query.trim()}".`
                      : `No organisations are tagged as ${noun} yet.`}
                </p>
                {!showAll && (
                  <button
                    type="button"
                    className="text-xs text-blue-600 underline"
                    onMouseDown={e => { e.preventDefault(); setShowAll(true); setSearched(false); }}
                  >
                    Search all organisations instead
                  </button>
                )}
                <p className="text-xs text-neutral-500">
                  An organisation only appears here once it exists in Factree. Its type is set
                  under Organisation Settings.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The supplying nursery is not a free choice among nurseries — it must be one
 * that already holds an active propagation licence for this cultivar. The
 * server refuses anything else, so this offers exactly that set rather than
 * the whole nursery register followed by a rejection on submit.
 *
 * It is a list, not a typeahead: the set is small by nature, and seeing all
 * three of them is more useful than being asked to guess a name.
 */
function SupplyingNurseryPicker({
  cultivarId,
  cultivarName,
  value,
  onChange,
  onIssuePropagationLicence,
}: {
  cultivarId: number;
  cultivarName: string;
  value?: number;
  onChange: (id: number, name: string) => void;
  onIssuePropagationLicence: () => void;
}) {
  const [nurseries, setNurseries] = useState<AuthorisedNursery[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const orgTz = useOrgTimezone();

  useEffect(() => {
    if (!cultivarId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await cultivarService.listAuthorisedNurseries(cultivarId);
        if (cancelled) return;
        setNurseries(res.success && res.data ? res.data : []);
      } catch {
        if (!cancelled) setError('Could not load the authorised nurseries. Try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cultivarId]);

  // A selection made before the list arrived (or under a different cultivar)
  // must not survive into a payload the server will reject.
  useEffect(() => {
    if (loading || !value) return;
    if (!nurseries.some(n => n.id === value)) onChange(0, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, nurseries, value]);

  return (
    <div className="space-y-1">
      <label className="block text-sm font-semibold text-neutral-700">Supplying Nursery *</label>
      <p className="text-xs text-neutral-500">
        Only nurseries entitled to produce this cultivar can supply the trees, so only those
        are listed — either from a Nursery Propagation licence naming it, or from a signed
        master sublicence with this cultivar selected on it.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking which nurseries are licensed...
        </div>
      ) : error ? (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{error}</div>
      ) : nurseries.length === 0 ? (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 space-y-2">
          <p className="font-medium">
            No nursery is licensed to propagate {cultivarName || 'this cultivar'}.
          </p>
          <p className="text-amber-700">
            A planting licence names the nursery that will supply the trees, and that nursery
            has to hold the right to produce them. Either issue a Nursery Propagation licence
            for this cultivar, or — if the nursery has a signed master sublicence — tick this
            cultivar for it on the cultivar's edit screen.
          </p>
          <button
            type="button"
            onClick={onIssuePropagationLicence}
            className="text-sm font-medium text-amber-900 underline"
          >
            Switch to Nursery Propagation
          </button>
        </div>
      ) : (
        <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 overflow-hidden">
          {nurseries.map(n => {
            const selected = n.id === value;
            return (
              <button
                key={n.id}
                type="button"
                aria-pressed={selected}
                onClick={() => onChange(n.id, n.name)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 ${
                  selected ? 'bg-blue-50' : 'hover:bg-neutral-50'
                }`}
              >
                <Building2 className={`h-4 w-4 mt-0.5 flex-shrink-0 ${selected ? 'text-blue-600' : 'text-neutral-400'}`} />
                <span className="min-w-0 flex-1">
                  <span className={`block text-sm font-medium ${selected ? 'text-blue-900' : 'text-neutral-900'}`}>
                    {n.name}
                  </span>
                  <span className="block text-xs text-neutral-500">
                    {n.source === 'master_sublicence'
                      ? `Master sublicence #${n.master_license_id}${n.group_code ? ` · group ${n.group_code}` : ''}`
                      : `Propagation licence #${n.license_id}`}
                    {n.end_date
                      ? ` — expires ${formatLocalDateInTz(n.end_date, orgTz)}`
                      : ' — no end date'}
                    {n.region ? ` · ${n.region}` : ''}
                  </span>
                </span>
                {selected && <CheckCircle2 className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function IssueLicense() {
  const { id } = useParams<{ id: string }>();
  const cultivarId = Number(id);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ id: number } | null>(null);

  const [cultivarName, setCultivarName] = useState('');
  // A licensed tree is a composite: the scion is the licensed cultivar, the
  // rootstock is a cultivar in its own right and may be protected by someone
  // else entirely. The server reads its protection details itself to fill the
  // {{rootstock_*}} fields.
  const [rootstockId, setRootstockId] = useState<number | null>(null);
  const [families, setFamilies] = useState<ContractFamily[]>([]);
  const [groups, setGroups] = useState<SublicenceGroup[]>([]);
  const [groupId, setGroupId] = useState<number | 0>(0);

  const [familyId, setFamilyId] = useState<number | 0>(0);
  const [tier, setTier] = useState<Tier>('nursery_propagation');
  const [licenseeOrgId, setLicenseeOrgId] = useState<number | undefined>();
  const [licenseeOrgName, setLicenseeOrgName] = useState('');
  const [nurseryOrgId, setNurseryOrgId] = useState<number | undefined>();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [notifyUserId, setNotifyUserId] = useState<number | 0>(0);
  const [region, setRegion] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [maxTrees, setMaxTrees] = useState<string>('');
  const [tolerancePct, setTolerancePct] = useState<string>('5');
  const [mergeValues, setMergeValues] = useState<Record<string, string>>({});

  // The exact text that would be sent. Issuing hands its fingerprint back, so
  // what was read here is what goes — any change to the form clears it.
  const [preview, setPreview] = useState<LicensePreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [customFields, setCustomFields] = useState<string[]>([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cRes, fRes, gRes] = await Promise.all([
          cultivarService.getCultivar(cultivarId),
          contractService.list({ for_cultivar_id: cultivarId, published_only: true }),
          cultivarService.listSublicenceGroups()
        ]);
        if (cRes.success && cRes.data) setCultivarName(cRes.data.trade_name || cRes.data.name);
        if (fRes.success) setFamilies(fRes.data ?? []);
        if (gRes.success && gRes.data) setGroups(gRes.data);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [cultivarId]);

  useEffect(() => {
    setNotifyUserId(0);
    setContacts([]);
    if (!licenseeOrgId) return;
    let cancelled = false;
    (async () => {
      setContactsLoading(true);
      try {
        const res = await cultivarService.getOrgCultivarContacts(licenseeOrgId);
        if (!cancelled && res.success && res.data) setContacts(res.data);
      } catch { /* ignore */ } finally {
        if (!cancelled) setContactsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [licenseeOrgId]);

  const master = isMasterTier(tier);
  const tierFamilies = useMemo(() => families.filter(f => f.license_tier === tier), [families, tier]);
  const selectedGroup = groups.find(g => g.id === groupId) || null;
  const selectedFamily = master ? null : tierFamilies.find(f => f.id === familyId) || null;

  // One published contract for a tier is plainly the one: select it rather
  // than make somebody pick from a list of one.
  useEffect(() => {
    if (!master && tierFamilies.length === 1) setFamilyId(tierFamilies[0].id);
  }, [master, tierFamilies]);

  const payload = useMemo((): IssueLicenseInput => ({
    family_id: master ? null : (familyId || null),
    // A master names a group instead. Sending the cultivar as well would be
    // rejected by the shape constraint, and rightly — it would read as an
    // agreement covering one cultivar AND a whole group.
    cultivar_id: master ? null : cultivarId,
    sublicence_group_id: master ? (groupId || null) : null,
    rootstock_cultivar_id: master ? null : rootstockId,
    licensee_org_id: licenseeOrgId || 0,
    license_tier: tier,
    supplying_nursery_org_id: tier === 'orchard_planting' ? (nurseryOrgId || null) : null,
    region: region || null,
    start_date: startDate || null,
    end_date: endDate || null,
    max_trees: master ? null : (maxTrees ? parseInt(maxTrees, 10) : null),
    tolerance_pct: (tier === 'nursery_propagation' || master) ? 0 : parseFloat(tolerancePct || '5'),
    merge_values: mergeValues,
  }), [master, familyId, cultivarId, groupId, rootstockId, licenseeOrgId, tier, nurseryOrgId, region, startDate, endDate, maxTrees, tolerancePct, mergeValues]);

  // Anything that changes the text invalidates what was previewed.
  useEffect(() => { setPreview(null); }, [payload]);

  const missing = (): string | null => {
    if (master && !groupId) return 'Pick the sublicence group this master covers';
    if (master && selectedGroup && !selectedGroup.contract_family_id) return `Group ${selectedGroup.code} has no master contract set`;
    if (!master && !familyId) return 'Pick the contract to send';
    if (!licenseeOrgId) return 'Pick a licensee organisation';
    if (tier === 'orchard_planting' && !nurseryOrgId) return 'Pick a supplying nursery';
    return null;
  };

  const runPreview = async () => {
    setError(null);
    const m = missing();
    if (m) { setError(m); return; }
    setPreviewing(true);
    try {
      const res = await cultivarService.previewLicense(payload);
      if (res.success) {
        setPreview(res.data);
        setCustomFields(res.data.custom_fields);
      } else {
        setError(res.error.message);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not build the agreement');
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async () => {
    setError(null);
    if (!preview) { setError('Review the agreement first'); return; }
    if (!notifyUserId) { setError('Pick a recipient for the licence email — terms can only be sent to a named contact'); return; }
    setSubmitting(true);
    try {
      const res = await cultivarService.issueLicenseFromTemplate({
        ...payload,
        notify_user_id: notifyUserId,
        expected_content_hash: preview.content_hash,
      });
      if (res.success) setSuccess({ id: res.data.id });
      else setError(res.error.message);
    } catch (e) {
      // The text moved between review and send (a new version was published).
      // Clear the preview so the sender reads the new text before trying again.
      if (e instanceof ApiError && e.code === 'CONTRACT_CHANGED') setPreview(null);
      setError(e instanceof Error ? e.message : 'Issue failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Issue Licence" icon={<Scale className="h-6 w-6 text-white" />} />
        <div className="flex justify-center py-20"><Loader2 className="h-10 w-10 animate-spin text-primary-600" /></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="Licence Issued" icon={<Scale className="h-6 w-6 text-white" />} />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card><CardContent className="p-8 text-center space-y-4">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h2 className="text-xl font-bold">Licence #{success.id} issued</h2>
            <p className="text-neutral-600 text-sm">
              The licensee organisation can now open the agreement and sign it.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              <Button variant="primary" onClick={() => navigate(`/cultivar/licences/${success.id}/agreement`)}>
                Open agreement
              </Button>
              <Button variant="outline" onClick={() => navigate(`/cultivar/${cultivarId}`)}>
                Back to cultivar
              </Button>
            </div>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* A master covers a group, not this cultivar, so naming the cultivar
          in the subtitle would say the opposite of what the page does. */}
      <PageHeader
        title="Issue Licence"
        subtitle={master ? 'Master nursery sublicence — covers a group, not one cultivar' : cultivarName}
        icon={<Scale className="h-6 w-6 text-white" />}
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        <Card>
          <CardHeader><CardTitle>Licence & contract</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label htmlFor="issue-tier" className="block text-sm font-semibold text-neutral-700 mb-1">Licence</label>
              <select
                id="issue-tier"
                value={tier}
                onChange={e => { setTier(e.target.value as Tier); setFamilyId(0); }}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
              >
                <option value="nursery_master_sublicence">Master Nursery Sublicence (a group)</option>
                <option value="nursery_propagation">Nursery Propagation</option>
                <option value="orchard_planting">Orchard Planting</option>
              </select>
              {master && (
                <p className="mt-1.5 text-xs text-neutral-500">
                  A master admits a nursery to a group and to nothing else. It names no
                  cultivar — cultivars reach the nursery one at a time, as you flag each one
                  sublicensable and place it in this group from its edit screen.
                </p>
              )}
            </div>

            {master ? (
              <div>
                <label htmlFor="issue-group" className="block text-sm font-semibold text-neutral-700 mb-1">
                  Sublicence group *
                </label>
                {groups.length === 0 ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    You have no sublicence groups yet.{' '}
                    <Link to="/cultivar/sublicence-groups" className="underline">Set one up</Link> first —
                    a master has to be written against one.
                  </div>
                ) : (
                  <select
                    id="issue-group"
                    value={groupId}
                    onChange={e => setGroupId(Number(e.target.value))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={0}>— pick a group —</option>
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>{g.code} — {g.name}</option>
                    ))}
                  </select>
                )}
                {/* The group decides the contract. There is no second choice
                    to get wrong. */}
                {selectedGroup && (
                  selectedGroup.contract_family_id ? (
                    <p className="mt-2 text-sm text-neutral-700 flex items-start gap-2">
                      <FileSignature className="h-4 w-4 mt-0.5 text-neutral-400 flex-shrink-0" />
                      <span>
                        Sends <strong>{selectedGroup.contract_family_name}</strong>
                        {selectedGroup.contract_current_version
                          ? ` v${selectedGroup.contract_current_version}, the version in force.`
                          : ' — which has no published version, so it cannot be sent yet.'}
                      </span>
                    </p>
                  ) : (
                    <div className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                      Group {selectedGroup.code} has no master contract set.{' '}
                      <Link to="/cultivar/sublicence-groups" className="underline">Set it on the group</Link> first.
                    </div>
                  )
                )}
              </div>
            ) : (
              <div>
                <label htmlFor="issue-contract" className="block text-sm font-semibold text-neutral-700 mb-1">Contract *</label>
                {tierFamilies.length === 0 ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    No published {tier === 'orchard_planting' ? 'orchard planting' : 'propagation'} contract.{' '}
                    <Link to="/cultivar/contracts" className="underline">Write and publish one</Link>.
                  </div>
                ) : (
                  <select
                    id="issue-contract"
                    value={familyId}
                    onChange={e => setFamilyId(Number(e.target.value))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={0}>— pick a contract —</option>
                    {tierFamilies.map(f => (
                      <option key={f.id} value={f.id}>{f.name}{f.cultivar_id ? ' (this cultivar only)' : ''}</option>
                    ))}
                  </select>
                )}
                {selectedFamily && (
                  <p className="mt-1.5 text-xs text-neutral-500">
                    Sends v{selectedFamily.current_version}, the version in force. Older versions cannot be sent.
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Parties</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <OrgSearch
              label="Licensee Organisation *"
              hint={
                master
                  ? 'The nursery signing the master agreement for this group'
                  : tier === 'nursery_propagation'
                    ? 'The nursery that will propagate this cultivar'
                    : 'The orchard that will plant this cultivar'
              }
              // 'grower' was folded into 'orchard' by the canonical vocabulary
              // (migration 20260915-06), so one value covers both now.
              stakeholderTypeFilter={tier === 'orchard_planting' ? ORCHARD_TYPE_FILTER : NURSERY_TYPE_FILTER}
              typeLabel={tier === 'orchard_planting' ? 'orchard' : 'nursery'}
              value={licenseeOrgId}
              selectedName={licenseeOrgName}
              onChange={(id, name) => { setLicenseeOrgId(id || undefined); setLicenseeOrgName(name); }}
            />

            {licenseeOrgId && (
              <div className="space-y-1">
                <label htmlFor="issue-recipient" className="block text-sm font-semibold text-neutral-700">Send licence email to *</label>
                <p className="text-xs text-neutral-500">
                  The named person at {licenseeOrgName || 'the licensee'} who will receive the agreement and sign it.
                  Licence terms are only ever sent to this one contact.
                </p>
                {contactsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-neutral-500 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading contacts…
                  </div>
                ) : contacts.length === 0 ? (
                  <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
                    This organisation has no cultivar contact to send the licence to. A licence
                    can't be issued until they have at least one active cultivar user.
                  </div>
                ) : (
                  <select
                    id="issue-recipient"
                    value={notifyUserId}
                    onChange={e => setNotifyUserId(Number(e.target.value))}
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value={0}>— pick a recipient —</option>
                    {contacts.map(c => (
                      <option key={c.id} value={c.id}>
                        {`${c.first_name} ${c.last_name}`.trim() || c.email} — {c.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {tier === 'orchard_planting' && (
              <>
                <SupplyingNurseryPicker
                  cultivarId={cultivarId}
                  cultivarName={cultivarName}
                  value={nurseryOrgId}
                  onChange={(id) => { setNurseryOrgId(id || undefined); }}
                  onIssuePropagationLicence={() => { setTier('nursery_propagation'); setFamilyId(0); }}
                />
                <CultivarPicker
                  label="Rootstock"
                  hint="The stock these trees are worked on. Fills the rootstock clauses of the deed, and records the second component of the tree — it may be protected by a different rights holder."
                  placeholder="Search rootstock cultivars..."
                  cultivarRole="rootstock"
                  value={rootstockId}
                  onChange={(picked: PickedCultivar | null) => setRootstockId(picked ? picked.id : null)}
                />
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Terms</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Region" value={region} onChange={e => setRegion(e.target.value)} placeholder="e.g. South Australia" />
              {/* A master grants no trees. The quantity belongs on the
                  per-order schedule issued under it, which is where the number
                  is actually known — putting a figure here would cap a whole
                  group against a guess made before a single order existed. */}
              {!master && (
                <Input
                  label={tier === 'nursery_propagation' ? 'Trees authorised to propagate' : 'Trees licensed to plant'}
                  type="number"
                  value={maxTrees}
                  onChange={e => setMaxTrees(e.target.value)}
                  placeholder="e.g. 1000"
                  hint={tier === 'nursery_propagation' ? 'An exact figure — no margin is applied.' : undefined}
                />
              )}
              <Input label="Start date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
              <Input label="End date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
              {/* Margin exists to absorb the error in a hand-drawn cultivar
                  area. A propagation licence is never measured off a polygon,
                  so the field would be meaningless there — and offering it
                  invites somebody to widen a grant that should be exact. */}
              {tier === 'orchard_planting' && (
                <Input
                  label="Mapping tolerance %"
                  type="number"
                  value={tolerancePct}
                  onChange={e => setTolerancePct(e.target.value)}
                  hint="Margin for polygon error when areas are drawn, not ordered. Set 0 for an exact grant."
                />
              )}
            </div>

            {tier === 'nursery_propagation' && (
              <p className="text-xs text-neutral-500">
                A propagation licence grants exactly the number of trees stated. Tolerance applies
                only to planting licences whose area is measured from a drawn map.
              </p>
            )}

            {master && (
              <p className="text-xs text-neutral-500">
                A master authorises no trees on its own. Each order placed against a cultivar in
                this group issues its own schedule under this agreement, for exactly the trees
                that order asks for.
              </p>
            )}

            {customFields.length > 0 && (
              <div>
                <p className="block text-sm font-semibold text-neutral-700 mb-2">
                  Fields this contract asks you to fill in
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {customFields.map((f) => (
                    <Input
                      key={f}
                      label={f.replace(/_/g, ' ')}
                      value={mergeValues[f] || ''}
                      onChange={e => setMergeValues({ ...mergeValues, [f]: e.target.value })}
                    />
                  ))}
                </div>
                <p className="text-xs text-neutral-500 mt-2">
                  Anything left empty prints as a line to fill in by hand. Party names, dates,
                  quantities and cultivar details are filled in from the licence itself.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {preview && (
          <Card>
            <CardHeader><CardTitle>The agreement that will be sent</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-neutral-700">
                <strong>{preview.family.name}</strong> v{preview.version.version}
                {preview.version.change_summary ? ` — ${preview.version.change_summary}` : ''}
              </p>
              <Fingerprint hash={preview.content_hash} label="Fingerprint of this text" />
              <RightsNotice state={preview.cultivar_rights_state} audience="issuer" />
              {preview.blank_fields.length > 0 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  Left to fill in by hand: {preview.blank_fields.map(f => f.replace(/_/g, ' ')).join(', ')}.
                </p>
              )}
              <pre className="whitespace-pre-wrap break-words font-sans text-sm text-neutral-800 bg-neutral-50 border border-neutral-200 rounded p-3 max-h-[32rem] overflow-y-auto">
                {preview.merged_terms}
              </pre>
            </CardContent>
          </Card>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm flex gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(`/cultivar/${cultivarId}`)}>Cancel</Button>
          {!preview ? (
            <Button variant="primary" onClick={runPreview} disabled={previewing}>
              {previewing ? 'Building…' : 'Review agreement'}
            </Button>
          ) : (
            <Button variant="primary" onClick={submit} disabled={submitting || !notifyUserId}>
              {submitting ? 'Sending…' : 'Send for signature'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
